import { countries, Country, deliveryZoneCodes } from '@noths/polaris-client-localisation';
import { DropDownOption } from '@noths/polaris-client-ribbons-design-system';
import {
  selectUserCountryCode,
  selectUserCurrencyCode,
} from '@noths/polaris-client-user-configuration';
import { createSelector } from '@reduxjs/toolkit';

import { selectDeliveryOptionsForZone, selectSelectedZone } from 'src/redux/deliveryZoneOptions';
import { selectCountriesDeliveredTo } from 'src/redux/product';
import { selectProductDeliveryOptions } from 'src/redux/selectors';
import { DeliveryOption } from 'src/types';
import { DeliveryOptionInfo } from '../components/DeliveryBlock';
import { getDeliveryOptionName } from '../utils/getDeliveryOptionName';

export const selectProductDeliveryOptionsForZone = createSelector(
  [selectProductDeliveryOptions, selectSelectedZone],
  (productDeliveryOptions, selectedZone): DeliveryOption[] => {
    const zoneId = parseInt(
      Object.keys(deliveryZoneCodes).find((key) => deliveryZoneCodes[key] === selectedZone) || '',
    );

    return productDeliveryOptions?.filter(({ zone }) => zone.id === zoneId) ?? [];
  },
);

export const selectDecoratedDeliveryOptions = createSelector(
  [selectDeliveryOptionsForZone, selectUserCurrencyCode],
  (deliveryOptionsForZone, userCurrency = 'GBP'): DeliveryOptionInfo[] =>
    deliveryOptionsForZone?.map(({ optionCode, prices, date, exactDate, max, min }) => ({
      date,
      exactDate,
      max,
      min,
      optionCode,
      price: prices.find(({ currencyCode }) => currencyCode === userCurrency)!,
      name: getDeliveryOptionName(optionCode, date),
    })) ?? [],
);

const createDropDownOptionFromCountry = ({
  name,
  countryCode,
  deliveryZoneCode,
}: Country): DropDownOption => ({
  optionLabel: name,
  optionValue: `${countryCode}|${deliveryZoneCode}${name === 'United Kingdom' ? `|UK` : ''}`,
});

const isUsersCountry = (userCountryCode?: Country['countryCode']) => ({ countryCode }: Country) =>
  countryCode === userCountryCode;

export const selectCountriesForDropdown = createSelector(
  [selectCountriesDeliveredTo, selectUserCountryCode],
  (countriesDeliveredTo, userCountryCode): DropDownOption[] => {
    const countryDropDownOptions = countriesDeliveredTo.map(createDropDownOptionFromCountry);

    if (countriesDeliveredTo.some(isUsersCountry(userCountryCode))) {
      return countryDropDownOptions;
    }

    const selectedCountryData = countries.find(isUsersCountry(userCountryCode));

    return (selectedCountryData
      ? [createDropDownOptionFromCountry(selectedCountryData)]
      : []
    ).concat(countryDropDownOptions);
  },
);
